import * as i0 from '@angular/core';
import { inject, Input, ViewChild, ViewEncapsulation, Component, NgModule } from '@angular/core';
import { IS_BROWSER, E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils';
import * as d3 from 'd3';
import { CommonModule } from '@angular/common';

class E11ChartPercentageComponent {
    constructor(changeDetector) {
        this.changeDetector = changeDetector;
        this.data = 0;
        this.id = ''; // id
        this.size = '100%'; // rem, px, or percentage as string. Ex: 4rem, 200px, 75%
        this.colorCircleHex = '#A4A4A4';
        this.colorLineHex = '#0080D3';
        this.colorTextHex = '#4F4F4F';
        this.autoChartId = '';
        this.imageFallBack = false;
        this.isBrowser = inject(IS_BROWSER);
        this.autoChartId = this.idForPercentChart();
    }
    ngOnInit() {
        this.id ? (this.id = this.id + '_' + this.autoChartId) : (this.id = this.autoChartId);
    }
    idForPercentChart() {
        const length = 16;
        const id = Math.random()
            .toString(36)
            .replace(/[^a-z]+/g, '')
            .substr(0, length);
        return id;
    }
    ngAfterViewInit() {
        setTimeout(() => {
            if (this.id) {
                this.createChart();
            }
        });
        this.changeDetector.detectChanges();
    }
    ngOnChanges(changes) {
        if (!changes || !changes.data)
            return;
        if (changes.data.firstChange === false) {
            this.refreshSvg();
            setTimeout(() => {
                this.createChart();
            });
        }
    }
    createChart() {
        if (!this.isBrowser)
            return;
        const element = this.percentageChart.nativeElement;
        const classCircle = d3
            .select('#' + this.id)
            .selectAll("[class$='circle']")
            .attr('fill', 'none')
            .attr('stroke-width', '1')
            .attr('d', 'M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831');
        const classLine = d3
            .select('#' + this.id)
            .selectAll("[class$='line']")
            .attr('fill', 'none')
            .attr('stroke-dasharray', '' + this.data + ', 100')
            .attr('stroke-width', '3')
            .attr('stroke-linecap', 'round')
            .attr('d', 'M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831');
        this.svg = d3
            .select('#' + this.id)
            .select('svg')
            .append('text')
            .attr('text-anchor', 'middle')
            .attr('class', 'percent')
            .attr('fill', this.colorTextHex)
            .attr('dominant-baseline', 'middle')
            .attr('dy', '50%')
            .attr('dx', '50%');
        var progress = 0, allocated = this.data, total = 100, formatPercent = d3.format('.0%');
        var percentComplete = this.svg;
        var i = d3.interpolate(progress, allocated / total);
        d3.select('#' + this.id)
            .select('svg')
            .transition()
            .duration(2000)
            .tween('progress', function () {
            return function (t) {
                progress = i(t);
                percentComplete.text(formatPercent(progress));
            };
        });
    }
    refreshSvg() {
        this.removeSvgAttribute('g');
        this.removeSvgAttribute('text');
    }
    removeSvgAttribute(attr) {
        this.svg = d3
            .select('#' + this.id)
            .select('svg')
            .select(attr)
            .remove();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ChartPercentageComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11ChartPercentageComponent, selector: "e11-chart-percentage", inputs: { data: "data", id: "id", size: "size", colorCircleHex: "colorCircleHex", colorLineHex: "colorLineHex", colorTextHex: "colorTextHex", imageUrl: "imageUrl" }, viewQueries: [{ propertyName: "percentageChart", first: true, predicate: ["percentageChart"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"container-svg\" id=\"{{ id }}\" #percentageChart>\n  <svg class=\"e11-chart-percentage\" viewBox=\"0 0 36 36\" style=\"width: {{ size }}; height: {{ size }};\">\n    <path class=\"circle\" style=\"stroke: {{ colorCircleHex }}\"></path>\n    <path class=\"line\" style=\"stroke: {{ colorLineHex }}\"></path>\n  </svg>\n  <div class=\"image\" style=\"background-image: url({{ imageUrl }})\"></div>\n</div>\n", styles: [".container-svg{position:relative;display:flex;align-items:center;justify-content:center}.container-svg svg{position:relative;max-width:100%}.container-svg svg.e11-chart-percentage .circle{animation:fade-in 2s ease-out forwards}.container-svg svg.e11-chart-percentage text.percent{opacity:1;stroke-width:2;font-size:55%}.container-svg .image{display:flex;position:absolute;width:82%;height:82%;background-size:cover;background-position:center;border-radius:50%;opacity:1;transition:opacity .2s ease-in-out 0s}.container-svg:hover .image{opacity:.1;transition:opacity .2s ease-in-out 0s}@keyframes progress{0%{stroke-dasharray:0 100}}@keyframes fade-in{0%{opacity:0}to{opacity:1}}@keyframes fade-out{0%{opacity:1}to{opacity:0}}\n"], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ChartPercentageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-chart-percentage', encapsulation: ViewEncapsulation.None, template: "<div class=\"container-svg\" id=\"{{ id }}\" #percentageChart>\n  <svg class=\"e11-chart-percentage\" viewBox=\"0 0 36 36\" style=\"width: {{ size }}; height: {{ size }};\">\n    <path class=\"circle\" style=\"stroke: {{ colorCircleHex }}\"></path>\n    <path class=\"line\" style=\"stroke: {{ colorLineHex }}\"></path>\n  </svg>\n  <div class=\"image\" style=\"background-image: url({{ imageUrl }})\"></div>\n</div>\n", styles: [".container-svg{position:relative;display:flex;align-items:center;justify-content:center}.container-svg svg{position:relative;max-width:100%}.container-svg svg.e11-chart-percentage .circle{animation:fade-in 2s ease-out forwards}.container-svg svg.e11-chart-percentage text.percent{opacity:1;stroke-width:2;font-size:55%}.container-svg .image{display:flex;position:absolute;width:82%;height:82%;background-size:cover;background-position:center;border-radius:50%;opacity:1;transition:opacity .2s ease-in-out 0s}.container-svg:hover .image{opacity:.1;transition:opacity .2s ease-in-out 0s}@keyframes progress{0%{stroke-dasharray:0 100}}@keyframes fade-in{0%{opacity:0}to{opacity:1}}@keyframes fade-out{0%{opacity:1}to{opacity:0}}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { percentageChart: [{
                type: ViewChild,
                args: ['percentageChart', { static: true }]
            }], data: [{
                type: Input
            }], id: [{
                type: Input
            }], size: [{
                type: Input
            }], colorCircleHex: [{
                type: Input
            }], colorLineHex: [{
                type: Input
            }], colorTextHex: [{
                type: Input
            }], imageUrl: [{
                type: Input
            }] } });

class E11ChartPercentageModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ChartPercentageModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11ChartPercentageModule, declarations: [E11ChartPercentageComponent], imports: [CommonModule, E11UtilModule], exports: [E11ChartPercentageComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ChartPercentageModule, imports: [CommonModule, E11UtilModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ChartPercentageModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11ChartPercentageComponent],
                    imports: [CommonModule, E11UtilModule],
                    exports: [E11ChartPercentageComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11ChartPercentageComponent, E11ChartPercentageModule };
